import { useEffect, useRef, useState } from "react";
import Seo from "@/components/base/Seo";
import AdminPageHeader from "../components/AdminPageHeader";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { useAuth } from "@/context/AuthContext";
import { uploadBrandingImage, getAdminSettings, resyncAllImages } from "@/services/adminApi";
import { API_BASE_URL, IS_MOCK } from "@/services/config";
import { collectMirrorImageUrls } from "@/utils/assetCatalog";
import { SITE } from "@/config/site";
import SocialSharePreview from "@/components/feature/SocialSharePreview";
import { toSquarePngFile, FAVICON_SIZE } from "@/utils/imageNormalize";
import type { SiteSettings } from "@/types";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500";

interface ToggleRowProps {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  tone?: "primary" | "secondary";
}

function ToggleRow({ label, hint, checked, onChange, tone = "primary" }: ToggleRowProps) {
  const activeClass = tone === "secondary" ? "bg-secondary-500" : "bg-primary-500";
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4 border-t border-background-100 py-4 first:border-t-0">
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-foreground-900">{label}</span>
        <span className="mt-0.5 block text-xs text-foreground-500">{hint}</span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer ${
          checked ? activeClass : "bg-background-300"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-background-50 transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        ></span>
      </button>
    </label>
  );
}

const SOCIAL_FIELDS: { key: keyof SiteSettings["social"]; label: string; icon: string; placeholder: string }[] = [
  { key: "facebook", label: "Facebook", icon: "ri-facebook-fill", placeholder: "https://facebook.com/yourpage" },
  { key: "instagram", label: "Instagram", icon: "ri-instagram-line", placeholder: "https://instagram.com/yourpage" },
  { key: "x", label: "X (Twitter)", icon: "ri-twitter-x-fill", placeholder: "https://x.com/yourpage" },
  { key: "linkedin", label: "LinkedIn", icon: "ri-linkedin-fill", placeholder: "https://linkedin.com/company/you" },
  { key: "youtube", label: "YouTube", icon: "ri-youtube-fill", placeholder: "https://youtube.com/@yourchannel" },
];

export default function AdminSettings() {
  const { settings, save, saving } = useSiteSettings();
  const { token } = useAuth();
  const [form, setForm] = useState<SiteSettings>(settings);
  const [adminAlerts, setAdminAlerts] = useState<Partial<SiteSettings>>({});
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState<"logo" | "logoDark" | "favicon" | "og" | null>(null);
  const [resyncing, setResyncing] = useState(false);
  const [resyncMsg, setResyncMsg] = useState("");
  const logoInput = useRef<HTMLInputElement>(null);
  const logoDarkInput = useRef<HTMLInputElement>(null);
  const faviconInput = useRef<HTMLInputElement>(null);
  const ogInput = useRef<HTMLInputElement>(null);

  // Keep the form in sync once the settings load / after a successful save.
  useEffect(() => {
    setForm({ ...settings, ...adminAlerts });
  }, [settings, adminAlerts]);

  // The lead-alert fields are admin-only (not in the public payload), so read
  // them from the admin settings endpoint and merge them in.
  useEffect(() => {
    let active = true;
    getAdminSettings(token)
      .then((s) => {
        if (!active) return;
        setAdminAlerts({
          alertAdminOnLead: s.alertAdminOnLead,
          alertOwnerOnEnquiry: s.alertOwnerOnEnquiry,
          leadDigestEnabled: s.leadDigestEnabled,
          adminAlertRecipients: s.adminAlertRecipients,
        });
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [token]);

  const patch = (part: Partial<SiteSettings>) => setForm((f) => ({ ...f, ...part }));

  const handleUpload = async (kind: "logo" | "logoDark" | "favicon" | "og", file: File | undefined) => {
    if (!file) return;
    setError("");
    try {
      setUploading(kind);
      // A favicon must be a small SQUARE PNG, so normalise it (centre-crop +
      // resize) before it leaves the browser — see utils/imageNormalize.
      const payload = kind === "favicon" ? await toSquarePngFile(file, FAVICON_SIZE) : file;
      // The dark-surface logo and the social share image upload as the same
      // branding "kind" on the server; only the destination field differs.
      const apiKind = kind === "favicon" ? "favicon" : kind === "og" ? "og" : "logo";
      const url = await uploadBrandingImage(payload, apiKind, token);
      const field: keyof SiteSettings =
        kind === "favicon" ? "faviconUrl" : kind === "logoDark" ? "logoDarkUrl" : kind === "og" ? "ogImageUrl" : "logoUrl";
      setForm((f) => ({ ...f, [field]: url }));
      // Persist immediately so a freshly uploaded logo / favicon is never lost
      // if the page is closed before the separate "Save settings" click.
      try {
        await save({ [field]: url });
      } catch {
        setError("The image uploaded, but saving the setting failed. Click “Save settings” to try again.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed. Please try another image.");
    } finally {
      setUploading(null);
    }
  };

  const handleResync = async () => {
    setResyncMsg("");
    setError("");
    try {
      setResyncing(true);
      const urls = collectMirrorImageUrls();
      if (urls.length === 0) {
        setResyncMsg("No CDN images were found in the project catalog.");
        return;
      }
      const accepted = await resyncAllImages(urls, token);
      setResyncMsg(
        accepted > 0
          ? `Re-sync started — ${accepted} image${accepted === 1 ? "" : "s"} queued. Missing files are copied in the background.`
          : "Everything is already up to date on the server.",
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start the image re-sync. Please try again.");
    } finally {
      setResyncing(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNotice("");
    setError("");
    try {
      await save(form);
      setNotice("Site settings saved.");
      setTimeout(() => setNotice(""), 2600);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save settings. Please try again.");
    }
  };

  return (
    <>
      <Seo title="System Settings" path="/admin/settings" />

      <AdminPageHeader
        icon="ri-settings-3-line"
        title="Website settings"
        subtitle="Branding, contact details and automation controls for the public site."
        actions={
          notice ? (
            <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
              <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
            </span>
          ) : undefined
        }
      />

      {error && (
        <div className="mt-4 flex items-start gap-3 rounded-md border border-secondary-200 bg-secondary-50 p-3.5 text-sm text-secondary-900">
          <i className="ri-error-warning-line mt-0.5 text-lg text-secondary-600"></i>
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={submit} className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Branding */}
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">Branding</h2>
            <p className="mt-1 text-xs text-foreground-500">
              The logo powers the header and footer (a light-on-dark variant is
              shown on dark surfaces), and the favicon is the browser-tab icon.
            </p>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="logoUrl" className={labelClass}>Logo</label>
                <div className="mb-2 flex h-20 items-center justify-center rounded-md border border-dashed border-background-300 bg-background-100/60 px-3">
                  {form.logoUrl ? (
                    <img src={form.logoUrl} alt="Logo preview" className="max-h-12 w-auto object-contain" />
                  ) : (
                    <span className="text-xs text-foreground-400">No logo set</span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    ref={logoInput}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) => handleUpload("logo", e.target.files?.[0])}
                  />
                  <button
                    type="button"
                    onClick={() => logoInput.current?.click()}
                    disabled={uploading === "logo"}
                    className="inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm font-semibold text-foreground-800 hover:border-primary-400 disabled:opacity-60 whitespace-nowrap cursor-pointer"
                  >
                    <i className={uploading === "logo" ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"}></i>
                    {uploading === "logo" ? "Uploading…" : "Upload logo"}
                  </button>
                  {form.logoUrl && (
                    <button
                      type="button"
                      onClick={() => patch({ logoUrl: "" })}
                      className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-secondary-700 hover:bg-secondary-50 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-delete-bin-line"></i> Remove
                    </button>
                  )}
                </div>
                <input
                  id="logoUrl"
                  name="logoUrl"
                  value={form.logoUrl}
                  onChange={(e) => patch({ logoUrl: e.target.value })}
                  placeholder="…or paste an image URL"
                  className={`${inputClass} mt-2`}
                />
              </div>

              <div>
                <label htmlFor="logoDarkUrl" className={labelClass}>Logo for dark backgrounds</label>
                <div className="mb-2 flex h-20 items-center justify-center rounded-md border border-dashed border-background-300 bg-foreground-950 px-3">
                  {form.logoDarkUrl ? (
                    <img src={form.logoDarkUrl} alt="Dark-background logo preview" className="max-h-12 w-auto object-contain" />
                  ) : (
                    <span className="text-xs text-background-300">Falls back to the main logo</span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    ref={logoDarkInput}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) => handleUpload("logoDark", e.target.files?.[0])}
                  />
                  <button
                    type="button"
                    onClick={() => logoDarkInput.current?.click()}
                    disabled={uploading === "logoDark"}
                    className="inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm font-semibold text-foreground-800 hover:border-primary-400 disabled:opacity-60 whitespace-nowrap cursor-pointer"
                  >
                    <i className={uploading === "logoDark" ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"}></i>
                    {uploading === "logoDark" ? "Uploading…" : "Upload dark logo"}
                  </button>
                  {form.logoDarkUrl && (
                    <button
                      type="button"
                      onClick={() => patch({ logoDarkUrl: "" })}
                      className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-secondary-700 hover:bg-secondary-50 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-delete-bin-line"></i> Remove
                    </button>
                  )}
                </div>
                <input
                  id="logoDarkUrl"
                  name="logoDarkUrl"
                  value={form.logoDarkUrl}
                  onChange={(e) => patch({ logoDarkUrl: e.target.value })}
                  placeholder="…or paste an image URL"
                  className={`${inputClass} mt-2`}
                />
                <p className="mt-1 text-xs text-foreground-500">
                  Shown automatically on the dark footer and sign-in panels. Leave blank to reuse the main logo.
                </p>
              </div>

              <div>
                <label htmlFor="faviconUrl" className={labelClass}>Favicon</label>
                <div className="mb-2 flex h-20 items-center justify-center rounded-md border border-dashed border-background-300 bg-background-100/60 px-3">
                  {form.faviconUrl ? (
                    <img src={form.faviconUrl} alt="Favicon preview" className="h-10 w-10 rounded object-contain" />
                  ) : (
                    <span className="text-xs text-foreground-400">No favicon set</span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    ref={faviconInput}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) => handleUpload("favicon", e.target.files?.[0])}
                  />
                  <button
                    type="button"
                    onClick={() => faviconInput.current?.click()}
                    disabled={uploading === "favicon"}
                    className="inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm font-semibold text-foreground-800 hover:border-primary-400 disabled:opacity-60 whitespace-nowrap cursor-pointer"
                  >
                    <i className={uploading === "favicon" ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"}></i>
                    {uploading === "favicon" ? "Uploading…" : "Upload favicon"}
                  </button>
                  {form.faviconUrl && (
                    <button
                      type="button"
                      onClick={() => patch({ faviconUrl: "" })}
                      className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-secondary-700 hover:bg-secondary-50 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-delete-bin-line"></i> Remove
                    </button>
                  )}
                </div>
                <input
                  id="faviconUrl"
                  name="faviconUrl"
                  value={form.faviconUrl}
                  onChange={(e) => patch({ faviconUrl: e.target.value })}
                  placeholder="…or paste an image URL"
                  className={`${inputClass} mt-2`}
                />
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="ogImageUrl" className={labelClass}>Social share image</label>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
                  <div>
                    <div className="mb-2 flex h-28 items-center justify-center overflow-hidden rounded-md border border-dashed border-background-300 bg-background-100/60">
                      {form.ogImageUrl ? (
                        <img
                          src={form.ogImageUrl}
                          alt="Social share image"
                          className="h-full w-full object-cover object-top"
                        />
                      ) : (
                        <span className="text-xs text-foreground-400">Falls back to the built-in brand card</span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        ref={ogInput}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        className="hidden"
                        onChange={(e) => handleUpload("og", e.target.files?.[0])}
                      />
                      <button
                        type="button"
                        onClick={() => ogInput.current?.click()}
                        disabled={uploading === "og"}
                        className="inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm font-semibold text-foreground-800 hover:border-primary-400 disabled:opacity-60 whitespace-nowrap cursor-pointer"
                      >
                        <i className={uploading === "og" ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"}></i>
                        {uploading === "og" ? "Uploading…" : "Upload share image"}
                      </button>
                      {form.ogImageUrl && (
                        <button
                          type="button"
                          onClick={() => patch({ ogImageUrl: "" })}
                          className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-secondary-700 hover:bg-secondary-50 whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-delete-bin-line"></i> Remove
                        </button>
                      )}
                    </div>
                    <input
                      id="ogImageUrl"
                      name="ogImageUrl"
                      value={form.ogImageUrl}
                      onChange={(e) => patch({ ogImageUrl: e.target.value })}
                      placeholder="…or paste an image URL"
                      className={`${inputClass} mt-2`}
                    />
                    <p className="mt-1 text-xs text-foreground-500">
                      Shown when any page is shared on Facebook, WhatsApp, X or LinkedIn. Best size 1200×630.
                    </p>
                  </div>
                  <div className="rounded-md border border-background-200 bg-background-100/50 p-3">
                    <SocialSharePreview
                      imageUrl={form.ogImageUrl}
                      title={form.siteName}
                      description={form.tagline}
                      url={SITE.url}
                    />
                  </div>
                </div>
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="siteName" className={labelClass}>Site name</label>
                <input
                  id="siteName"
                  name="siteName"
                  value={form.siteName}
                  onChange={(e) => patch({ siteName: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="tagline" className={labelClass}>Tagline</label>
                <input
                  id="tagline"
                  name="tagline"
                  value={form.tagline}
                  onChange={(e) => patch({ tagline: e.target.value })}
                  className={inputClass}
                />
              </div>
            </div>
          </section>

          {/* Image library — on-demand full re-sync */}
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-image-2-line text-primary-600"></i> Image library
                </h2>
                <p className="mt-1 text-xs text-foreground-500">
                  Copy every project image still hosted on the Readdy CDN into your own server's uploads
                  folder. It runs in the background, and an image you have already uploaded yourself is never
                  overwritten.
                </p>
                {resyncMsg && (
                  <p className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-xs font-medium text-accent-900">
                    <i className="ri-checkbox-circle-line text-accent-600"></i> {resyncMsg}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={handleResync}
                disabled={resyncing}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
              >
                <i className={resyncing ? "ri-loader-4-line animate-spin" : "ri-refresh-line"}></i>
                {resyncing ? "Starting…" : "Re-sync all images now"}
              </button>
            </div>
          </section>

          {/* Contact & localization */}
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">Contact &amp; localization</h2>
            <p className="mt-1 text-xs text-foreground-500">Shown across the footer, invoices and support channels.</p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contactEmail" className={labelClass}>Contact email</label>
                <input id="contactEmail" name="contactEmail" type="email" value={form.contactEmail} onChange={(e) => patch({ contactEmail: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label htmlFor="contactPhone" className={labelClass}>Contact phone</label>
                <input id="contactPhone" name="contactPhone" value={form.contactPhone} onChange={(e) => patch({ contactPhone: e.target.value })} className={inputClass} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="address" className={labelClass}>Address</label>
                <input id="address" name="address" value={form.address} onChange={(e) => patch({ address: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label htmlFor="gstin" className={labelClass}>GSTIN</label>
                <input id="gstin" name="gstin" value={form.gstin} onChange={(e) => patch({ gstin: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label htmlFor="currency" className={labelClass}>Currency</label>
                <select id="currency" name="currency" value={form.currency} onChange={(e) => patch({ currency: e.target.value })} className={`${inputClass} cursor-pointer`}>
                  <option value="INR (₹)">INR (₹)</option>
                  <option value="USD ($)">USD ($)</option>
                  <option value="AED (د.إ)">AED (د.إ)</option>
                </select>
              </div>
              <div>
                <label htmlFor="timezone" className={labelClass}>Timezone</label>
                <select id="timezone" name="timezone" value={form.timezone} onChange={(e) => patch({ timezone: e.target.value })} className={`${inputClass} cursor-pointer`}>
                  <option value="Asia/Kolkata (IST)">Asia/Kolkata (IST)</option>
                  <option value="Asia/Dubai (GST)">Asia/Dubai (GST)</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>
            </div>
          </section>

          {/* Social links */}
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">Social links</h2>
            <p className="mt-1 text-xs text-foreground-500">Leave a field blank to hide that icon from the footer.</p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {SOCIAL_FIELDS.map((field) => (
                <div key={field.key}>
                  <label htmlFor={`social-${field.key}`} className={labelClass}>
                    <i className={`${field.icon} mr-1`}></i>
                    {field.label}
                  </label>
                  <input
                    id={`social-${field.key}`}
                    name={`social_${field.key}`}
                    value={form.social[field.key]}
                    onChange={(e) => patch({ social: { ...form.social, [field.key]: e.target.value } })}
                    placeholder={field.placeholder}
                    className={inputClass}
                  />
                </div>
              ))}
            </div>
          </section>

          {/* Public pages */}
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">Category pages</h2>
            <p className="mt-1 text-xs text-foreground-500">
              Control the blocks that appear on the A to Z categories page.
            </p>
            <div className="mt-2">
              <ToggleRow
                label="Popular categories strip"
                hint="Show the “Popular categories” block near the top of the A to Z categories page."
                checked={form.showPopularCategories}
                onChange={(v) => patch({ showPopularCategories: v })}
                tone="secondary"
              />
            </div>
          </section>

          {/* Email notifications */}
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">Email notifications</h2>
            <p className="mt-1 text-xs text-foreground-500">
              Automated lead emails sent by the backend. These require a mail transport (SMTP) to be
              configured.
            </p>
            <div className="mt-2">
              <ToggleRow
                label="Admin lead alerts"
                hint="Email a distinct alert to the recipients below on every new lead."
                checked={form.alertAdminOnLead}
                onChange={(v) => patch({ alertAdminOnLead: v })}
              />
              <ToggleRow
                label="Notify business owners"
                hint="Email the listing owner when someone sends an enquiry on their profile."
                checked={form.alertOwnerOnEnquiry}
                onChange={(v) => patch({ alertOwnerOnEnquiry: v })}
              />
              <ToggleRow
                label="Daily leads digest"
                hint="Also send a once-a-day summary of new leads to the recipients below."
                checked={form.leadDigestEnabled}
                onChange={(v) => patch({ leadDigestEnabled: v })}
                tone="secondary"
              />
            </div>
            <div className="mt-4">
              <label htmlFor="adminAlertRecipients" className={labelClass}>
                Alert recipients
              </label>
              <input
                id="adminAlertRecipients"
                name="adminAlertRecipients"
                value={form.adminAlertRecipients}
                onChange={(e) => patch({ adminAlertRecipients: e.target.value })}
                placeholder="alerts@example.com, owner@example.com"
                className={inputClass}
              />
              <p className="mt-1 text-xs text-foreground-500">
                Comma-separated. Leave blank to send to the default form recipient only.
              </p>
            </div>
          </section>

          {/* Moderation */}
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">Moderation &amp; automation</h2>
            <div className="mt-2">
              <ToggleRow
                label="Auto-approve new listings"
                hint="Publish submissions instantly without manual review."
                checked={form.autoApproveListings}
                onChange={(v) => patch({ autoApproveListings: v })}
              />
              <ToggleRow
                label="Review moderation"
                hint="Hold new reviews until an admin approves them."
                checked={form.reviewsModeration}
                onChange={(v) => patch({ reviewsModeration: v })}
              />
              <ToggleRow
                label="Maintenance mode"
                hint="Show a maintenance notice and take the public site offline."
                checked={form.maintenanceMode}
                onChange={(v) => patch({ maintenanceMode: v })}
                tone="secondary"
              />
            </div>
          </section>
        </div>

        <aside className="space-y-4">
          <div className="rounded-lg border border-background-200 bg-background-100/60 p-5">
            <h2 className="font-heading text-sm font-bold text-foreground-950">Environment</h2>
            <dl className="mt-3 space-y-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">Data source</dt>
                <dd className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${IS_MOCK ? "bg-secondary-100 text-secondary-900" : "bg-accent-100 text-accent-900"}`}>
                  <i className={IS_MOCK ? "ri-flask-line" : "ri-server-line"}></i> {IS_MOCK ? "Demo" : "Live"}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">API base URL</dt>
                <dd className="max-w-[60%] truncate font-semibold text-foreground-800">{API_BASE_URL || "Not set"}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">Build version</dt>
                <dd className="font-semibold text-foreground-800">v1.0.0</dd>
              </div>
            </dl>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
            {saving ? "Saving…" : "Save settings"}
          </button>
        </aside>
      </form>
    </>
  );
}